import cn from "classnames";
import { DataGridColumnConfig, DataGridRowConfig } from "components/DataGrid/types";
import { isEmpty, isNil } from "lodash";
import { formatCellContent, getTableColumnCount } from "./utils";
import useMediaQuery from "components/utils/useMediaQuery";
import { Form } from "react-bootstrap";
import { DataGridNoResults } from "./DataGridNoResults";

export const DataGridBody: React.FC<{
    rows: DataGridRowConfig[];
    columns: DataGridColumnConfig[];
    hasActionsColumn?: boolean;
    isLoadingData?: boolean;
    canSelectRows?: boolean;
    handleSelectRow?: (row: DataGridRowConfig) => void;
    isRowSelected?: (row: DataGridRowConfig) => boolean;
    isFilterEmpty?: boolean;
    onFilterClear?: () => void;
}> = ({
    rows,
    columns,
    hasActionsColumn = true,
    isLoadingData,
    canSelectRows,
    handleSelectRow,
    isRowSelected,
    isFilterEmpty,
    onFilterClear,
}) => {
    const isMobile = useMediaQuery("(max-width: 768px)");

    if (rows.length === 0 && !isNil(onFilterClear) && !isNil(isFilterEmpty)) {
        return (
            <tbody className="data-grid-body data-grid-nothing-found">
                <tr className={cn({ "m-0 border-0": isMobile })}>
                    <td colSpan={getTableColumnCount(columns, hasActionsColumn) + 1} className="border-0">
                        <DataGridNoResults className="p-5" isFilterEmpty={isFilterEmpty} onFilterClear={onFilterClear} />
                    </td>
                </tr>
            </tbody>
        );
    }

    return (
        <tbody className="data-grid-body" aria-hidden={isLoadingData}>
            {rows.map((row, rowIndex) => (
                <tr
                    key={rowIndex}
                    className={cn("d-flex flex-column rounded-0 box-shadow-1 box-shadow-lg-0 my-2 my-lg-0 py-2 p-lg-0 d-lg-table-row", {
                        activated: canSelectRows && isRowSelected?.(row),
                        "border-bottom-lg border-top-lg-0 mt-0": rowIndex === 0,
                        "border-bottom-lg border-top-lg": rowIndex > 0,
                        "border-0": isMobile,
                    })}
                    onClick={() => canSelectRows && isMobile && handleSelectRow?.(row)}
                >
                    {canSelectRows && (
                        <td
                            className={cn({
                                "col-auto d-flex d-lg-table-cell select-column border border-top-0 border-start-0 rounded-0": !isMobile,
                                "border-bottom-0": !isMobile && rowIndex === rows.length - 1,
                                "border-0": isMobile,
                            })}
                        >
                            <Form.Check className={cn({ "pb-1": !isMobile })}>
                                <Form.Check.Input
                                    className={cn({ "m-0": isMobile })}
                                    checked={isRowSelected?.(row)}
                                    onChange={() => handleSelectRow?.(row)}
                                    aria-label={`Select row ${rowIndex + 1}`}
                                />
                            </Form.Check>
                        </td>
                    )}
                    {isMobile && row.mobileConfig && (
                        <td className={cn("pt-3 pb-4 border border-top-0", { "rounded-0": isMobile })}>
                            <div className="d-flex">
                                <div>
                                    {row.data[row.mobileConfig.title] && (
                                        <div className="fs-5 fw-medium pb-2 data-grid-title">{row.data[row.mobileConfig.title]}</div>
                                    )}
                                    {row.data[row.mobileConfig.subtitle] && (
                                        <div className="fs-6 fw-medium data-grid-subtitle text-secondary">
                                            {row.data[row.mobileConfig.subtitle]}
                                        </div>
                                    )}
                                </div>
                                {row.rendererPosHeader?.(rowIndex)}
                            </div>
                        </td>
                    )}
                    {columns.map((column, columnIndex) => {
                        const mobileConfigColumns = row.mobileConfig?.columns?.map((i) => i.toLowerCase());
                        const mobileConfigIncludesColumn = mobileConfigColumns?.includes(column.columnname.toLowerCase());

                        if (isMobile && row.mobileConfig !== undefined && !mobileConfigIncludesColumn) {
                            return null;
                        }

                        return (
                            <td
                                key={columnIndex}
                                className={cn(column.className, "d-flex flex-row col-md-2 gap-md-0 d-lg-table-cell fs-7", {
                                    "border border-top-0 border-end-0": !isMobile,
                                    "border-start-0 rounded-0": !isMobile && columnIndex === 0,
                                    "border-bottom-0": rowIndex === rows.length - 1,
                                    "pt-3 pb-0": isMobile,
                                    "col-sorted": !isEmpty(column.sort),
                                    "opacity-50": isLoadingData,
                                    "bg-light": canSelectRows && isMobile && isRowSelected?.(row),
                                })}
                            >
                                {isMobile && (
                                    <span
                                        className="text-capitalize d-flex align-items-start gap-2 w-100 flex-shrink-0"
                                        style={{ maxWidth: "11rem" }}
                                    >
                                        {column.name}
                                    </span>
                                )}
                                <span>
                                    {formatCellContent(
                                        column,
                                        row.data[Object.keys(row.data).find((i) => i.toLowerCase() === column.key.toLowerCase()) ?? ""],
                                        row
                                    )}
                                </span>
                            </td>
                        );
                    })}
                    {row.actions && (
                        <td
                            className={cn("actions-cell fs-7 px-2 py-3 position-sticky top-0 end-0 border", {
                                "border-top-0 border-end-0 rounded-0": !isMobile,
                                "border-bottom-0": !isMobile && rowIndex === rows.length - 1,
                                "border-bottom-0 border-top-1 mt-3": isMobile,
                            })}
                        >
                            <div className={cn("", { "d-flex align-items-center justify-content-center text-center": !isMobile })}>
                                {row.actions.map((action, index) => action.content(row, index))}
                            </div>
                        </td>
                    )}
                </tr>
            ))}
        </tbody>
    );
};
